import { useState, useMemo, useEffect } from 'react';
import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { formatPrice, formatPercentage } from '@/lib/utils';
import { 
  ShoppingCart, Plus, Minus, Utensils, Flame, ChefHat, 
  Package, Search, Filter, ChevronRight, Check, X,
  Calendar, Clock, User, Phone, Mail, MessageSquare, Truck, Printer, CheckCircle,
  Percent, Hash, Leaf, Snowflake, Beef, Carrot
} from 'lucide-react';
import type { RestaurantProduct, RestaurantCategory } from '@shared/schema';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Link } from 'wouter';
import { SimpleDatePicker } from '@/components/SimpleDatePicker';

// Icon mapping for categories
const iconMap: Record<string, any> = {
  Package, Beef, Carrot, ChefHat, Snowflake, Flame, Utensils, Leaf
};

// Color scheme configuration
const colorSchemeConfig: Record<string, any> = {
  amber: { 
    color: 'bg-amber-100 text-amber-800 border-amber-300', 
    bgColor: 'bg-amber-50',
    lightBg: 'bg-amber-50/50',
    mildColor: 'bg-amber-50 text-amber-700 hover:bg-amber-100',
    boldColor: 'bg-amber-600 text-white hover:bg-amber-700',
    borderColor: 'bg-amber-50 border-amber-200'
  },
  red: { 
    color: 'bg-red-100 text-red-800 border-red-300', 
    bgColor: 'bg-red-50',
    lightBg: 'bg-red-50/50',
    mildColor: 'bg-red-50 text-red-700 hover:bg-red-100',
    boldColor: 'bg-red-600 text-white hover:bg-red-700',
    borderColor: 'bg-red-50 border-red-200'
  },
  green: { 
    color: 'bg-green-100 text-green-800 border-green-300', 
    bgColor: 'bg-green-50',
    lightBg: 'bg-green-50/50',
    mildColor: 'bg-green-50 text-green-700 hover:bg-green-100',
    boldColor: 'bg-green-600 text-white hover:bg-green-700',
    borderColor: 'bg-green-50 border-green-200'
  },
  orange: { 
    color: 'bg-orange-100 text-orange-800 border-orange-300', 
    bgColor: 'bg-orange-50',
    lightBg: 'bg-orange-50/50',
    mildColor: 'bg-orange-50 text-orange-700 hover:bg-orange-100',
    boldColor: 'bg-orange-600 text-white hover:bg-orange-700',
    borderColor: 'bg-orange-50 border-orange-200'
  },
  blue: { 
    color: 'bg-blue-100 text-blue-800 border-blue-300', 
    bgColor: 'bg-blue-50',
    lightBg: 'bg-blue-50/50',
    mildColor: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
    boldColor: 'bg-blue-600 text-white hover:bg-blue-700',
    borderColor: 'bg-blue-50 border-blue-200'
  },
  purple: { 
    color: 'bg-purple-100 text-purple-800 border-purple-300', 
    bgColor: 'bg-purple-50',
    lightBg: 'bg-purple-50/50',
    mildColor: 'bg-purple-50 text-purple-700 hover:bg-purple-100',
    boldColor: 'bg-purple-600 text-white hover:bg-purple-700',
    borderColor: 'bg-purple-50 border-purple-200'
  },
  gray: { 
    color: 'bg-gray-100 text-gray-800 border-gray-300', 
    bgColor: 'bg-gray-50',
    lightBg: 'bg-gray-50/50',
    mildColor: 'bg-gray-50 text-gray-700 hover:bg-gray-100',
    boldColor: 'bg-gray-600 text-white hover:bg-gray-700',
    borderColor: 'bg-gray-50 border-gray-200'
  }
};

type CartItem = RestaurantProduct & { 
  quantity: number;
  originalPrice?: number;
  discountedPrice?: number;
  hasDiscount?: boolean;
  discountPercentage?: number;
  appliedTier?: string;
  customerTier?: string;
  isFullTierMatch?: boolean;
};

// Product Card Component
function RestaurantProductCard({ 
  product, 
  cartItem,
  onAddToCart,
  onUpdateQuantity,
  categoryConfig
}: { 
  product: any; // Can include discount info
  cartItem?: CartItem;
  onAddToCart: (product: any) => void;
  onUpdateQuantity: (productId: number, quantity: number) => void;
  categoryConfig?: any;
}) {
  const Icon = categoryConfig?.icon || Package;
  const [isExpanded, setIsExpanded] = useState(false);
  const [localQuantity, setLocalQuantity] = useState(cartItem?.quantity || product.minOrderQuantity);
  const [imageError, setImageError] = useState(false);
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -2 }}
      className="h-full"
    >
      <Card className={`${isExpanded ? '' : 'h-full'} hover:shadow-lg transition-all duration-300 border-2 border-gray-400 relative`}>
        {/* Discount Tier Badge */}
        {product.hasDiscount && product.appliedTier && !product.isFullTierMatch && (
          <div className="absolute top-1 left-1 z-10">
            <Badge className="bg-amber-100 text-amber-800 text-xs px-1 py-0.5">
              {product.appliedTier.replace('tier', 'T')} Applied
            </Badge>
          </div>
        )}
        <CardContent className="p-2">
          {/* Price - right aligned */}
          <div className="text-right mb-1">
            {product.hasDiscount ? (
              <>
                <span className="line-through text-gray-600 text-base">
                  ${product.originalPrice.toFixed(2)}
                </span>
                {' '}
                <span className="font-bold text-xl md:text-2xl text-green-900">
                  ${product.discountedPrice.toFixed(2)}
                </span>
                <span className="text-base text-gray-700 font-medium">/{product.unit}</span>
                <div className="text-sm text-emerald-700 font-bold">
                  Save {formatPercentage(product.discountPercentage || 0)}
                  {product.appliedTier && !product.isFullTierMatch && (
                    <span className="text-amber-600 ml-1">
                      ({product.appliedTier.replace('tier', 'T')})
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <span className="font-bold text-xl md:text-2xl text-green-900">
                  ${product.pricePerUnit.toFixed(2)}
                </span>
                <span className="text-base text-gray-700 font-medium">/{product.unit}</span>
              </>
            )}
          </div>


          {/* Product Image */}
          <div className="w-full h-20 md:h-24 bg-gray-100 mb-1 overflow-hidden rounded">
            {product.imageUrl && !imageError ? (
              <img 
                src={product.imageUrl} 
                alt={product.name}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Utensils className="w-6 h-6 text-gray-400" />
              </div>
            )}
          </div>

          {/* Product Info */}
          <h3 className="font-bold text-base md:text-lg mb-0.5 line-clamp-1 text-gray-900">{product.name}</h3>
          
          {/* Description with expandable */}
          <div className="mb-1">
            <AnimatePresence>
              {!isExpanded ? (
                <p className="text-gray-700 text-sm leading-tight line-clamp-2 min-h-[2.5rem] font-medium">
                  {product.description}
                </p>
              ) : (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-gray-700 text-sm leading-tight font-medium">
                    {product.description}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
            {/* Show expand button for longer descriptions (adjust threshold based on typical line length) */}
            {product.description && product.description.length > 60 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-emerald-700 hover:text-emerald-800 text-sm font-bold underline mt-1"
              >
                {isExpanded ? 'less' : 'more...'}
              </button>
            )}
          </div>

          {/* Format/Size Display */}
          <div className="text-sm text-gray-800 mb-1 font-medium">
            Format: {product.unit}{product.size ? ` (${product.size})` : ''}
          </div>

          {/* Min Order, Quantity Controls Row */}
          <div className="flex items-center justify-between gap-1">
            {/* Min Order - left aligned */}
            <div className="text-sm text-gray-700 flex-shrink-0 font-medium">
              Min: {product.minOrderQuantity}
            </div>
            
            {/* Quantity Controls - centered */}
            <div className="flex items-center gap-1 justify-center">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (cartItem) {
                    // If in cart, update cart quantity by 1
                    const newQty = Math.max(0, cartItem.quantity - 1);
                    onUpdateQuantity(product.id, newQty);
                  } else {
                    // If not in cart, update local quantity by 1
                    setLocalQuantity(Math.max(product.minOrderQuantity, localQuantity - 1));
                  }
                }}
                disabled={cartItem ? cartItem.quantity === 0 : localQuantity <= product.minOrderQuantity}
                className="h-8 w-8 p-0 bg-gray-50 hover:bg-gray-100"
              >
                <Minus className="w-3 h-3" />
              </Button>
              <div className="text-center min-w-[30px]">
                <span className="font-bold text-lg">{cartItem ? cartItem.quantity : localQuantity}</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (cartItem) {
                    // If in cart, update cart quantity by 1
                    onUpdateQuantity(product.id, cartItem.quantity + 1);
                  } else {
                    // If not in cart, update local quantity by 1
                    setLocalQuantity(localQuantity + 1);
                  }
                }}
                className="h-8 w-8 p-0 bg-gray-50 hover:bg-gray-100"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>

          {/* Add to Cart Button - separate row */}
          {(!cartItem || cartItem.quantity === 0) && (
            <div className="mt-1">
              <Button 
                className="bg-emerald-700 hover:bg-emerald-800 h-10 w-full text-base font-bold border-2 border-emerald-800"
                onClick={() => {
                  // Add to cart with the selected quantity
                  onAddToCart({ ...product, quantity: localQuantity } as CartItem);
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                <span>Add to Cart</span>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

// Cart Summary Component
function CartSummary({ 
  cart, 
  isDelivery,
  onToggleDelivery,
  onUpdateQuantity, 
  onCheckout,
  discountCode,
  discountCodeValid,
  discountInfo,
  onValidateCode,
  onClearCode,
  onDiscountCodeChange
}: { 
  cart: CartItem[];
  isDelivery: boolean;
  onToggleDelivery: (delivery: boolean) => void;
  onUpdateQuantity: (productId: number, quantity: number) => void;
  onCheckout: () => void;
  discountCode: string;
  discountCodeValid: boolean;
  discountInfo: { restaurantName: string; tier: string; discountPercentage: number; } | null;
  onValidateCode: () => void;
  onClearCode: () => void;
  onDiscountCodeChange: (code: string) => void;
}) {
  const { toast } = useToast();
  
  // Calculate totals based on whether discount code is applied
  const calculateTotals = () => {
    let subtotal = 0;
    let discountAmount = 0;
    
    cart.forEach(item => {
      // Use original price for subtotal calculation
      const originalPrice = item.originalPrice || item.pricePerUnit;
      const basePrice = originalPrice * item.quantity;
      subtotal += basePrice;
      
      // Calculate discount if we have a valid discount code and the item is eligible
      if (discountCodeValid && item.hasDiscount && item.discountPercentage) {
        const itemDiscount = basePrice * (item.discountPercentage / 100);
        discountAmount += itemDiscount;
      }
    });
    
    const deliveryFee = isDelivery && subtotal < FREE_DELIVERY_THRESHOLD ? DELIVERY_FEE : 0;
    const total = subtotal - discountAmount + deliveryFee;
    
    // Debug log
    console.log('Cart calculation:', {
      cart: cart.map(item => ({
        name: item.name,
        quantity: item.quantity,
        pricePerUnit: item.pricePerUnit,
        originalPrice: item.originalPrice,
        hasDiscount: item.hasDiscount,
        discountPercentage: item.discountPercentage,
        discountCodeValid
      })),
      subtotal,
      discountAmount,
      deliveryFee,
      total
    });
    
    return { subtotal, discountAmount, deliveryFee, total };
  };
  
  const { subtotal, discountAmount, deliveryFee, total } = calculateTotals();
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  if (cart.length === 0) {
    return (
      <Card className="sticky top-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Your Cart
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-8">Your cart is empty</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="sticky top-4">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Your Cart
          </span>
          <Badge>{itemCount} items</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Delivery/Pickup Toggle */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <Label className="hidden md:block text-sm font-medium mb-2">Order Type</Label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={!isDelivery ? "default" : "outline"}
              size="sm"
              onClick={() => onToggleDelivery(false)}
              className={!isDelivery ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            >
              <span className="hidden md:inline">PICKUP</span>
              <span className="md:hidden"><Package className="w-4 h-4 mr-1" />Pickup</span>
            </Button>
            <Button
              variant={isDelivery ? "default" : "outline"}
              size="sm"
              onClick={() => onToggleDelivery(true)}
              className={isDelivery ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            >
              <span className="hidden md:inline">DELIVERY</span>
              <span className="md:hidden"><Truck className="w-4 h-4 mr-1" />Delivery</span>
            </Button>
          </div>
          {isDelivery && (
            <p className="text-xs text-gray-600 mt-2">
              {subtotal >= 500 
                ? "✓ Free delivery on orders over $500" 
                : `Delivery fee: $25 (Free on orders over $500)`}
            </p>
          )}
        </div>

        {/* Discount Code Section */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <Label className="text-sm font-medium mb-2 flex items-center gap-2">
            <Hash className="w-4 h-4" />
            Discount Code
          </Label>
          {!discountCodeValid ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter discount code"
                  value={discountCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    onDiscountCodeChange(value.slice(0, 8));
                  }}
                  maxLength={8}
                  className="font-mono text-center text-lg h-12"
                />
                <Button
                  onClick={onValidateCode}
                  disabled={discountCode.length < 4}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Apply
                </Button>
              </div>
              <p className="text-xs text-gray-600">
                Enter your restaurant's discount code (4 characters) for special pricing
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-emerald-50 rounded border border-emerald-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">
                      {discountInfo?.restaurantName}
                    </p>
                    <p className="text-xs text-emerald-600">
                      Up to {formatPercentage(discountInfo?.discountPercentage || 0)} discount - best available rate applied
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onClearCode}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
          {cart.map((item) => {
            const itemSubtotal = (item.originalPrice || item.pricePerUnit) * item.quantity;
            const itemDiscount = item.hasDiscount && discountCodeValid && item.discountPercentage 
              ? itemSubtotal * (item.discountPercentage / 100)
              : 0;
            const itemTotal = itemSubtotal - itemDiscount;
            
            return (
              <div key={item.id} className="p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-base truncate">{item.name}</h4>
                    <p className="text-sm text-gray-700">
                      {item.hasDiscount && discountCodeValid ? (
                        <>
                          <span className="line-through text-gray-500">
                            {formatPrice(item.pricePerUnit)}
                          </span>
                          {' '}
                          <span className="text-emerald-600 font-medium">
                            {formatPrice(item.pricePerUnit * (1 - (item.discountPercentage || 0) / 100))}
                          </span>
                          /{item.unit}
                        </>
                      ) : (
                        <>{formatPrice(item.pricePerUnit)}/{item.unit}</>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onUpdateQuantity(item.id, Math.max(0, item.quantity - 1))}
                      className="h-8 w-8 p-0"
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="font-bold text-lg w-12 text-center">
                      {item.quantity}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                      className="h-8 w-8 p-0"
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onUpdateQuantity(item.id, 0)}
                      className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                {/* Item subtotal with discount */}
                <div className="flex justify-between items-center mt-1 pt-1 border-t border-gray-200">
                  <span className="text-sm text-gray-600 font-medium">Subtotal:</span>
                  <div className="text-right">
                    {itemDiscount > 0 ? (
                      <>
                        <span className="line-through text-gray-500 text-sm mr-1">
                          {formatPrice(itemSubtotal)}
                        </span>
                        <span className="font-bold text-base text-emerald-600">
                          {formatPrice(itemTotal)}
                        </span>
                        <span className="text-emerald-600 text-sm ml-1">
                          (-{formatPercentage(item.discountPercentage || 0)})
                        </span>
                      </>
                    ) : (
                      <span className="font-bold text-base">{formatPrice(itemSubtotal)}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <Separator className="my-3" />
        
        <div className="space-y-2">
          <div className="flex justify-between text-base font-medium">
            <span>Subtotal:</span>
            <span>{formatPrice(subtotal)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between text-base font-medium text-emerald-600">
              <span>Discount:</span>
              <span>-{formatPrice(discountAmount)}</span>
            </div>
          )}
          {deliveryFee > 0 && (
            <div className="flex justify-between text-base font-medium">
              <span>Delivery Fee:</span>
              <span>{formatPrice(deliveryFee)}</span>
            </div>
          )}
          <Separator className="my-2" />
          <div className="flex justify-between text-xl font-bold">
            <span>Total:</span>
            <span className="text-emerald-600">{formatPrice(total)}</span>
          </div>
          <Button 
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-lg font-bold py-6 border-2 border-emerald-800"
            onClick={onCheckout}
          >
            Proceed to Checkout
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Checkout Modal Component
function CheckoutModal({ 
  isOpen, 
  onClose,
  cart,
  subtotal,
  discountCode,
  discountAmount,
  deliveryFee,
  total,
  isDelivery,
  discountInfo,
  onSubmit
}: { 
  isOpen: boolean;
  onClose: () => void;
  cart: CartItem[];
  subtotal: number;
  discountCode: string | null;
  discountAmount: number;
  deliveryFee: number;
  total: number;
  isDelivery: boolean;
  discountInfo: {
    restaurantName: string;
    contactPerson?: string;
    email?: string;
    phone?: string;
    tier: string;
    discountPercentage: number;
  } | null;
  onSubmit: (orderData: any) => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Debug what the modal is receiving
  console.log('CheckoutModal props:', {
    subtotal,
    discountCode,
    discountAmount,
    deliveryFee,
    total,
    cartItems: cart.length,
    cartSample: cart[0] ? {
      name: cart[0].name,
      originalPrice: cart[0].originalPrice,
      discountedPrice: cart[0].discountedPrice,
      hasDiscount: cart[0].hasDiscount,
      discountPercentage: cart[0].discountPercentage
    } : null
  });
  const [formData, setFormData] = useState({
    restaurantName: discountInfo?.restaurantName || '',
    contactPerson: discountInfo?.contactPerson || '',
    email: discountInfo?.email || '',
    phone: discountInfo?.phone || '',
    deliveryDate: '',
    deliveryTime: '9-12',
    notes: '',
    isDelivery: isDelivery,
    // Delivery specific fields
    deliveryAddress: '',
    deliveryCity: '',
    deliveryPostalCode: '',
    deliveryInstructions: ''
  });

  // Update form data when discountInfo changes
  useEffect(() => {
    if (discountInfo) {
      setFormData(prev => ({
        ...prev,
        restaurantName: discountInfo.restaurantName || prev.restaurantName,
        contactPerson: discountInfo.contactPerson || prev.contactPerson,
        email: discountInfo.email || prev.email,
        phone: discountInfo.phone || prev.phone
      }));
    }
  }, [discountInfo]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    const orderData = {
      ...formData,
      orderItems: JSON.stringify(cart.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        pricePerUnit: item.pricePerUnit,
        unit: item.unit
      }))),
      subtotalAmount: subtotal,
      discountCode: discountCode,
      discountAmount: discountAmount,
      deliveryFee: deliveryFee,
      totalAmount: total
    };
    
    onSubmit(orderData);
    
    // Reset after 1.5 seconds
    setTimeout(() => {
      setIsSubmitting(false);
    }, 1500);
  };

  // Set minimum date to tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Complete Your Order</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Restaurant Information */}
          <div>
            <h3 className="font-bold text-lg mb-3">Restaurant Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="restaurantName">Restaurant Name *</Label>
                <Input
                  id="restaurantName"
                  name="organization"
                  autoComplete="organization"
                  value={formData.restaurantName}
                  onChange={(e) => setFormData({ ...formData, restaurantName: e.target.value })}
                  required
                  disabled={!!discountInfo?.restaurantName}
                  className={discountInfo?.restaurantName ? "bg-gray-100" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactPerson">Contact Person</Label>
                <Input
                  id="contactPerson"
                  name="name"
                  autoComplete="name"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  disabled={!!discountInfo?.contactPerson}
                  className={discountInfo?.contactPerson ? "bg-gray-100" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!!discountInfo?.email}
                  className={discountInfo?.email ? "bg-gray-100" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="tel"
                  type="tel"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  disabled={!!discountInfo?.phone}
                  className={discountInfo?.phone ? "bg-gray-100" : ""}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Pickup/Delivery Toggle */}
          <div>
            <div className="flex gap-2 mb-4">
              <Button
                type="button"
                variant={!formData.isDelivery ? "default" : "outline"}
                className={`flex-1 ${!formData.isDelivery ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                onClick={() => setFormData({ ...formData, isDelivery: false })}
              >
                <Package className="w-4 h-4 mr-2" />
                Pickup (Free)
              </Button>
              <Button
                type="button"
                variant={formData.isDelivery ? "default" : "outline"}
                className={`flex-1 ${formData.isDelivery ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                onClick={() => setFormData({ ...formData, isDelivery: true })}
              >
                <Truck className="w-4 h-4 mr-2" />
                <span className="hidden md:inline">Delivery (Free for &gt;$500)</span>
                <span className="md:hidden">Delivery</span>
              </Button>
            </div>

            {/* Pickup Information */}
            {!formData.isDelivery && (
              <div>
                <h3 className="font-bold text-lg mb-3">Pickup Information</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="font-bold text-base mb-1">Pickup Location:</p>
                  <p className="text-base text-gray-700">Tops Supermarket</p>
                  <p className="text-base text-gray-700">#183 - 1623 Centre St, Calgary, AB T2E 8S7</p>
                  <p className="text-base text-gray-700 mt-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Store Hours: Mon-Sun 9:00 AM - 6:00 PM
                  </p>
                </div>
                <div className="space-y-4">
                  <SimpleDatePicker
                    label="Pickup Date"
                    value={formData.deliveryDate}
                    onChange={(date) => setFormData({ ...formData, deliveryDate: date })}
                    minDate={tomorrow}
                  />
                  <div className="space-y-2">
                    <Label className="text-base font-medium">Preferred Pickup Time</Label>
                    <RadioGroup
                      value={formData.deliveryTime}
                      onValueChange={(value) => setFormData({ ...formData, deliveryTime: value })}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="9-12" id="morning-pickup" />
                        <Label htmlFor="morning-pickup" className="text-base">9:00 AM - 12:00 PM</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="12-4" id="afternoon-pickup" />
                        <Label htmlFor="afternoon-pickup" className="text-base">12:00 PM - 4:00 PM</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
              </div>
            )}

            {/* Delivery Information */}
            {formData.isDelivery && (
              <div>
                <h3 className="font-bold text-lg mb-3">Delivery Information</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="deliveryAddress" className="text-base font-medium">Delivery Address</Label>
                      <Input
                        id="deliveryAddress"
                        name="street-address"
                        autoComplete="street-address"
                        value={formData.deliveryAddress}
                        onChange={(e) => setFormData({ ...formData, deliveryAddress: e.target.value })}
                        placeholder="123 Restaurant Street"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="deliveryCity" className="text-base font-medium">City</Label>
                        <Input
                          id="deliveryCity"
                          name="address-level2"
                          autoComplete="address-level2"
                          value={formData.deliveryCity}
                          onChange={(e) => setFormData({ ...formData, deliveryCity: e.target.value })}
                          placeholder="Calgary"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="deliveryPostalCode" className="text-base font-medium">Postal Code</Label>
                        <Input
                          id="deliveryPostalCode"
                          name="postal-code"
                          autoComplete="postal-code"
                          value={formData.deliveryPostalCode}
                          onChange={(e) => setFormData({ ...formData, deliveryPostalCode: e.target.value })}
                          placeholder="T2E 8S7"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <SimpleDatePicker
                      label="Delivery Date"
                      value={formData.deliveryDate}
                      onChange={(date) => setFormData({ ...formData, deliveryDate: date })}
                      minDate={tomorrow}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Preferred Delivery Time</Label>
                    <RadioGroup
                      value={formData.deliveryTime}
                      onValueChange={(value) => setFormData({ ...formData, deliveryTime: value })}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="9-12" id="morning-delivery" />
                        <Label htmlFor="morning-delivery">9:00 AM - 12:00 PM</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="12-4" id="afternoon-delivery" />
                        <Label htmlFor="afternoon-delivery">12:00 PM - 4:00 PM</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deliveryInstructions">Delivery Instructions (Optional)</Label>
                    <Textarea
                      id="deliveryInstructions"
                      value={formData.deliveryInstructions}
                      onChange={(e) => setFormData({ ...formData, deliveryInstructions: e.target.value })}
                      placeholder="Loading dock location, special instructions, etc."
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Order Summary */}
          <div>
            <h3 className="font-bold text-lg mb-3">Order Summary</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {cart.map((item) => (
                <div key={item.id} className="flex justify-between text-base">
                  <span className="font-medium">{item.name} × {item.quantity} {item.unit}</span>
                  <div className="text-right">
                    {item.hasDiscount && item.discountPercentage > 0 ? (
                      <>
                        <span className="line-through text-gray-500 mr-2">
                          {formatPrice((item.originalPrice || item.pricePerUnit) * item.quantity)}
                        </span>
                        <span className="text-emerald-600 font-bold text-base">
                          {formatPrice((item.discountedPrice || item.pricePerUnit) * item.quantity)}
                        </span>
                      </>
                    ) : (
                      <span className="font-medium">{formatPrice((item.originalPrice || item.pricePerUnit) * item.quantity)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Separator className="my-3" />
            <div className="space-y-1">
              <div className="flex justify-between text-base font-medium">
                <span>Subtotal:</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-base font-medium text-emerald-600">
                  <span>Discount Savings {discountCode ? `(${discountCode})` : ''}:</span>
                  <span>-{formatPrice(discountAmount)}</span>
                </div>
              )}
              {formData.isDelivery && deliveryFee > 0 && (
                <div className="flex justify-between text-base font-medium">
                  <span>Delivery Fee:</span>
                  <span>{formatPrice(deliveryFee)}</span>
                </div>
              )}
              {formData.isDelivery && deliveryFee === 0 && (
                <div className="flex justify-between text-base font-medium text-green-600">
                  <span>Delivery Fee:</span>
                  <span>FREE (order over $500)</span>
                </div>
              )}
              <div className="flex justify-between text-base">
                <span className="font-bold">ORDER TYPE:</span>
                <span className="font-bold">{formData.isDelivery ? 'Delivery' : 'Pickup'}</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between font-bold text-xl">
                <span>Total:</span>
                <span className="text-emerald-600">{formatPrice(total)}</span>
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-base font-medium">Additional Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Special delivery instructions, dietary requirements, etc."
            />
          </div>

          {/* Call confirmation message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Phone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-base text-blue-800">
              <p className="font-bold mb-1">Order Confirmation</p>
              <p className="font-medium">We'll call you within 1 business day to confirm your order and finalize details.</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Place Order
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Order Success Modal with Details
function OrderSuccessModal({ 
  isOpen, 
  onClose,
  orderDetails
}: { 
  isOpen: boolean;
  onClose: () => void;
  orderDetails: any;
}) {
  if (!orderDetails) return null;

  // Generate calendar links
  const generateCalendarLinks = () => {
    const eventDate = new Date(orderDetails.deliveryDate);
    const [startHour] = orderDetails.deliveryTime.split('-').map((h: string) => parseInt(h));
    
    // Set proper start and end times
    const startTime = new Date(eventDate);
    startTime.setHours(startHour, 0, 0, 0);
    
    const endTime = new Date(eventDate);
    if (orderDetails.deliveryTime === '9-12') {
      endTime.setHours(12, 0, 0, 0);
    } else {
      endTime.setHours(16, 0, 0, 0); // 4 PM
    }
    
    const title = `Tops100 Order`;
    
    const location = orderDetails.isDelivery 
      ? `${orderDetails.deliveryAddress}, ${orderDetails.deliveryCity}, ${orderDetails.deliveryPostalCode}`
      : "Tops Supermarket, #183 - 1623 Centre St, Calgary, AB T2E 8S7";
    
    const details = `Order #${orderDetails.orderNumber}\\nItems: ${orderDetails.cart.length} items\\nTotal: $${orderDetails.total.toFixed(2)}\\nContact: ${orderDetails.phone}`;
    
    // Format dates for ICS (YYYYMMDDTHHmmssZ)
    const formatICSDate = (date: Date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
    };
    
    const icsStart = formatICSDate(startTime);
    const icsEnd = formatICSDate(endTime);
    const icsNow = formatICSDate(new Date());
    
    // Google Calendar URL
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${icsStart}/${icsEnd}&details=${encodeURIComponent(details.replace(/\\n/g, '\n'))}&location=${encodeURIComponent(location)}`;
    
    // ICS file content for Apple/Outlook
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Tops Supermarket//Restaurant Orders//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${orderDetails.orderNumber}@tops-supermarket.com
DTSTAMP:${icsNow}
DTSTART:${icsStart}
DTEND:${icsEnd}
SUMMARY:${title}
LOCATION:${location}
DESCRIPTION:${details}
STATUS:CONFIRMED
SEQUENCE:0
BEGIN:VALARM
TRIGGER:-PT1H
ACTION:DISPLAY
DESCRIPTION:Restaurant order ${orderDetails.isDelivery ? 'delivery' : 'pickup'} in 1 hour
END:VALARM
END:VEVENT
END:VCALENDAR`;
    
    const icsBlob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const icsUrl = URL.createObjectURL(icsBlob);
    
    return { googleUrl, icsUrl, icsContent };
  };
  
  const { googleUrl, icsUrl } = generateCalendarLinks();
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="py-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <DialogTitle className="text-2xl mb-2 text-center">Order Placed Successfully!</DialogTitle>
          
          {/* Order Number */}
          <div className="bg-emerald-50 rounded-lg p-4 mb-6 text-center">
            <p className="text-sm text-gray-600">Order Number</p>
            <p className="text-2xl font-bold text-emerald-700">{orderDetails.orderNumber}</p>
          </div>
          
          {/* Order Details */}
          <div className="space-y-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold mb-2 flex items-center">
                <User className="w-4 h-4 mr-2" />
                Customer Information
              </h3>
              <div className="text-sm space-y-1">
                <p><span className="text-gray-600">Restaurant:</span> {orderDetails.restaurantName}</p>
                <p><span className="text-gray-600">Contact:</span> {orderDetails.contactPerson}</p>
                <p><span className="text-gray-600">Email:</span> {orderDetails.email}</p>
                <p><span className="text-gray-600">Phone:</span> {orderDetails.phone}</p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold mb-2 flex items-center">
                {orderDetails.isDelivery ? <Truck className="w-4 h-4 mr-2" /> : <Package className="w-4 h-4 mr-2" />}
                {orderDetails.isDelivery ? 'Delivery' : 'Pickup'} Information
              </h3>
              <div className="text-sm space-y-1">
                <p><span className="text-gray-600">Date:</span> {new Date(orderDetails.deliveryDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                <p><span className="text-gray-600">Time:</span> {orderDetails.deliveryTime === '9-12' ? '9:00 AM - 12:00 PM' : '12:00 PM - 4:00 PM'}</p>
                {orderDetails.isDelivery ? (
                  <>
                    <p><span className="text-gray-600">Address:</span> {orderDetails.deliveryAddress}</p>
                    <p><span className="text-gray-600">City:</span> {orderDetails.deliveryCity}, {orderDetails.deliveryPostalCode}</p>
                    {orderDetails.deliveryInstructions && (
                      <p><span className="text-gray-600">Instructions:</span> {orderDetails.deliveryInstructions}</p>
                    )}
                  </>
                ) : (
                  <>
                    <p><span className="text-gray-600">Location:</span> Tops Supermarket</p>
                    <p><span className="text-gray-600">Address:</span> #183 - 1623 Centre St, Calgary, AB T2E 8S7</p>
                  </>
                )}
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold mb-2 flex items-center">
                <ShoppingCart className="w-4 h-4 mr-2" />
                Order Summary
              </h3>
              <div className="text-sm space-y-2 max-h-32 overflow-y-auto">
                {orderDetails.cart.map((item: any) => {
                  const originalTotal = (item.originalPrice || item.pricePerUnit) * item.quantity;
                  const hasDiscount = item.hasDiscount && item.discountPercentage > 0;
                  const discountedTotal = hasDiscount 
                    ? originalTotal * (1 - item.discountPercentage / 100)
                    : originalTotal;
                  
                  return (
                    <div key={item.id} className="flex justify-between items-center">
                      <span>{item.name} × {item.quantity} {item.unit}</span>
                      <div className="text-right">
                        {hasDiscount ? (
                          <>
                            <span className="line-through text-gray-500 mr-2">
                              ${originalTotal.toFixed(2)}
                            </span>
                            <span className="font-medium text-emerald-600">
                              ${discountedTotal.toFixed(2)}
                            </span>
                          </>
                        ) : (
                          <span className="font-medium">${originalTotal.toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <Separator className="my-2" />
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span>${orderDetails.subtotal.toFixed(2)}</span>
                </div>
                {orderDetails.discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>Discount:</span>
                    <span>-${orderDetails.discountAmount.toFixed(2)}</span>
                  </div>
                )}
                {orderDetails.deliveryFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Delivery Fee:</span>
                    <span>${orderDetails.deliveryFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold">
                  <span>Total:</span>
                  <span className="text-emerald-600">${orderDetails.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Calendar Links */}
          <div className="mb-6">
            <h3 className="font-semibold mb-2 flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Add to Calendar
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(googleUrl, '_blank')}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Google Calendar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = icsUrl;
                  link.download = `order-${orderDetails.orderNumber}.ics`;
                  link.click();
                }}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Apple/Outlook
              </Button>
            </div>
          </div>
          
          {/* Next Steps */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold mb-2 text-blue-900">What's Next?</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• You'll receive a confirmation email shortly</li>
              <li>• We'll call you within 2 hours to confirm your order</li>
              <li>• You can add this order to your calendar using the buttons above</li>
              {orderDetails.isDelivery 
                ? <li>• Our driver will call 30 minutes before delivery</li>
                : <li>• Please bring your order number when picking up</li>
              }
            </ul>
          </div>
          
          <div className="flex gap-2">
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={onClose}>
              Continue Shopping
            </Button>
            <Button 
              variant="outline" 
              onClick={() => window.print()}
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function RestaurantOrdering() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isDelivery, setIsDelivery] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [discountCode, setDiscountCode] = useState<string>('');
  const [discountCodeValid, setDiscountCodeValid] = useState(false);
  const [discountInfo, setDiscountInfo] = useState<{
    restaurantName: string;
    contactPerson?: string;
    email?: string;
    phone?: string;
    tier: string;
    discountPercentage: number;
  } | null>(null);
  const [productsWithDiscount, setProductsWithDiscount] = useState<any[]>([]);
  const { toast } = useToast();

  // Set page title
  useEffect(() => {
    document.title = 'Restaurant Ordering - Tops100';
  }, []);

  // Fetch categories from the database
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['restaurant-categories'],
    queryFn: async () => {
      return await apiRequest('GET', '/api/restaurant/categories') as RestaurantCategory[];
    }
  });

  // Fetch delivery settings
  const { data: deliverySettings } = useQuery({
    queryKey: ['delivery-settings'],
    queryFn: async () => {
      return await apiRequest('GET', '/api/settings/delivery') as {
        deliveryFee: number;
        freeDeliveryThreshold: number;
        deliveryEnabled: boolean;
      };
    },
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  });

  // Use delivery settings with fallback defaults
  const DELIVERY_FEE = deliverySettings?.deliveryFee ?? 25;
  const FREE_DELIVERY_THRESHOLD = deliverySettings?.freeDeliveryThreshold ?? 500;

  // Build category configuration from database
  const RESTAURANT_CATEGORY_CONFIG = useMemo(() => {
    const config: Record<string, any> = {};
    categories.forEach(category => {
      const colorConfig = colorSchemeConfig[category.colorScheme] || colorSchemeConfig.gray;
      config[category.name] = {
        label: category.name,
        ...colorConfig,
        icon: iconMap[category.icon || 'Package'] || Package
      };
    });
    return config;
  }, [categories]);

  // Fetch restaurant products (with discount if code is valid)
  const { data: products, isLoading, refetch: refetchProducts } = useQuery({
    queryKey: ['restaurant-products', discountCodeValid ? discountCode : ''],
    queryFn: async () => {
      if (discountCodeValid && discountCode) {
        console.log('Fetching products with discount code:', discountCode);
        const response = await apiRequest('GET', `/api/restaurant/products-with-discount/${discountCode}`);
        console.log('Products with discount response:', response);
        
        // Ensure each product has the discount fields properly set
        const productsWithDiscount = response.products.map((product: any) => ({
          ...product,
          originalPrice: product.originalPrice || product.pricePerUnit,
          discountedPrice: product.discountedPrice || product.pricePerUnit,
          hasDiscount: product.hasDiscount || false,
          // Convert decimal to percentage (0.07 -> 7)
          discountPercentage: product.discountPercentage ? product.discountPercentage * 100 : 0,
          appliedTier: product.appliedTier || null,
          customerTier: product.customerTier || null,
          isFullTierMatch: product.isFullTierMatch || false
        }));
        
        setProductsWithDiscount(productsWithDiscount);
        return productsWithDiscount;
      } else {
        console.log('Fetching regular products without discount');
        return await apiRequest('GET', '/api/restaurant/products') as RestaurantProduct[];
      }
    },
    enabled: true // Always enabled, but only refetches when discountCodeValid changes
  });

  // Validate discount code
  const validateDiscountCode = async () => {
    if (!discountCode || discountCode.length !== 4) {
      toast({ 
        title: 'Invalid code', 
        description: 'Please enter a 4 character discount code',
        variant: 'destructive' 
      });
      return;
    }

    try {
      const response = await apiRequest('POST', '/api/restaurant/validate-code', { code: discountCode });
      
      if (response.valid) {
        setDiscountCodeValid(true);
        setDiscountInfo({
          restaurantName: response.restaurantName,
          contactPerson: response.contactPerson,
          email: response.email,
          phone: response.phone,
          tier: response.tier,
          discountPercentage: response.discountPercentage * 100 // Convert to percentage
        });
        toast({ 
          title: 'Code validated!',
          description: response.message,
          className: 'bg-green-50 border-green-200'
        });
        // Refetch products with discount prices
        refetchProducts();
      } else {
        setDiscountCodeValid(false);
        setDiscountInfo(null);
        toast({ 
          title: 'Invalid code', 
          description: response.message,
          variant: 'destructive' 
        });
      }
    } catch (error) {
      toast({ 
        title: 'Error validating code', 
        description: 'Please try again',
        variant: 'destructive' 
      });
    }
  };

  // Clear discount code
  const clearDiscountCode = () => {
    setDiscountCode('');
    setDiscountCodeValid(false);
    setDiscountInfo(null);
    setProductsWithDiscount([]);
    
    // Reset cart items to original prices
    setCart(prevCart => prevCart.map(cartItem => ({
      ...cartItem,
      hasDiscount: false,
      discountPercentage: 0,
      appliedTier: null,
      customerTier: null,
      isFullTierMatch: false,
      discountedPrice: cartItem.pricePerUnit
    })));
    
    refetchProducts();
  };

  // Update cart items with discount information when products are refetched
  useEffect(() => {
    if (discountCodeValid && products && cart.length > 0) {
      console.log('Updating cart with discount information...');
      
      // Update cart items with discount information from the fetched products
      setCart(prevCart => prevCart.map(cartItem => {
        const updatedProduct = products.find((p: any) => p.id === cartItem.id);
        if (updatedProduct) {
          console.log(`Updating cart item ${cartItem.name} with discount info:`, {
            hasDiscount: updatedProduct.hasDiscount,
            discountPercentage: updatedProduct.discountPercentage,
            originalPrice: updatedProduct.originalPrice,
            discountedPrice: updatedProduct.discountedPrice
          });
          
          return {
            ...cartItem,
            originalPrice: updatedProduct.originalPrice || cartItem.pricePerUnit,
            discountedPrice: updatedProduct.discountedPrice || cartItem.pricePerUnit,
            hasDiscount: Boolean(updatedProduct.hasDiscount),
            discountPercentage: updatedProduct.discountPercentage || 0,
            appliedTier: updatedProduct.appliedTier || null,
            customerTier: updatedProduct.customerTier || null,
            isFullTierMatch: Boolean(updatedProduct.isFullTierMatch)
          };
        }
        return cartItem;
      }));
    }
  }, [products, discountCodeValid]);

  // Submit order mutation
  const submitOrderMutation = useMutation({
    mutationFn: async (orderData: any) => {
      return await apiRequest('POST', '/api/restaurant/order', orderData);
    },
    onError: () => {
      toast({ 
        title: 'Failed to place order', 
        description: 'Please try again or contact support.',
        variant: 'destructive' 
      });
    }
  });

  // Filter products
  const filteredProducts = useMemo(() => {
    if (!products) return [];

    return products.filter((product: any) => {
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      
      return matchesCategory && product.active;
    });
  }, [products, selectedCategory]);

  // Group products by category
  const productsByCategory = useMemo(() => {
    const grouped: Record<string, RestaurantProduct[]> = {};
    
    filteredProducts.forEach((product: any) => {
      if (!grouped[product.category]) {
        grouped[product.category] = [];
      }
      grouped[product.category].push(product);
    });
    
    return grouped;
  }, [filteredProducts]);

  const addToCart = (product: any) => {
    const quantity = product.quantity || product.minOrderQuantity;
    
    // Debug what we're receiving
    console.log('Adding to cart - Full product object:', product);
    console.log('Adding to cart - Key fields:', {
      name: product.name,
      originalPrice: product.originalPrice,
      discountedPrice: product.discountedPrice,
      pricePerUnit: product.pricePerUnit,
      hasDiscount: product.hasDiscount,
      discountPercentage: product.discountPercentage,
      appliedTier: product.appliedTier,
      customerTier: product.customerTier
    });
    
    const cartItem: CartItem = {
      ...product,
      quantity,
      pricePerUnit: product.originalPrice || product.pricePerUnit, // Keep original price for calculations
      originalPrice: product.originalPrice || product.pricePerUnit,
      discountedPrice: product.discountedPrice || product.pricePerUnit,
      hasDiscount: Boolean(product.hasDiscount), // Ensure boolean
      discountPercentage: product.discountPercentage || 0, // Already in percentage format from API
      appliedTier: product.appliedTier || null,
      customerTier: product.customerTier || null,
      isFullTierMatch: Boolean(product.isFullTierMatch)
    };
    
    console.log('Cart item created:', {
      name: cartItem.name,
      quantity: cartItem.quantity,
      pricePerUnit: cartItem.pricePerUnit,
      originalPrice: cartItem.originalPrice,
      discountedPrice: cartItem.discountedPrice,
      hasDiscount: cartItem.hasDiscount,
      discountPercentage: cartItem.discountPercentage
    });
    
    setCart(prev => [...prev, cartItem]);
  };

  const updateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      setCart(prev => prev.filter(item => item.id !== productId));
    } else {
      setCart(prev => prev.map(item => 
        item.id === productId ? { ...item, quantity } : item
      ));
    }
  };

  const getCartItem = (productId: number) => {
    return cart.find(item => item.id === productId);
  };

  // Calculate totals including discounts
  const calculateTotals = () => {
    let subtotal = 0;
    let discountAmount = 0;
    
    cart.forEach(item => {
      // Use original price for subtotal calculation
      const originalPrice = item.originalPrice || item.pricePerUnit;
      const basePrice = originalPrice * item.quantity;
      subtotal += basePrice;
      
      // Calculate discount if we have a valid discount code and the item is eligible
      if (discountCodeValid && item.hasDiscount && item.discountPercentage > 0) {
        // discountPercentage is already in percentage format (e.g., 7 for 7%)
        const itemDiscount = basePrice * (item.discountPercentage / 100);
        discountAmount += itemDiscount;
        
        console.log(`Discount for ${item.name}:`, {
          basePrice,
          discountPercentage: item.discountPercentage,
          itemDiscount,
          calculation: `${basePrice} * (${item.discountPercentage} / 100) = ${itemDiscount}`
        });
      }
    });
    
    const deliveryFee = isDelivery && subtotal < FREE_DELIVERY_THRESHOLD ? DELIVERY_FEE : 0;
    const total = subtotal - discountAmount + deliveryFee;
    
    // Debug log
    console.log('Cart calculation summary:', {
      itemCount: cart.length,
      discountCodeValid,
      subtotal: subtotal.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      deliveryFee: deliveryFee.toFixed(2),
      total: total.toFixed(2),
      items: cart.map(item => ({
        name: item.name,
        quantity: item.quantity,
        pricePerUnit: item.pricePerUnit,
        originalPrice: item.originalPrice,
        hasDiscount: item.hasDiscount,
        discountPercentage: item.discountPercentage
      }))
    });
    
    return { subtotal, discountAmount, deliveryFee, total };
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast({ 
        title: 'Cart is empty', 
        description: 'Please add items to your cart before checking out.',
        variant: 'destructive' 
      });
      return;
    }
    setIsCheckoutOpen(true);
  };

  const handleSubmitOrder = (orderData: any) => {
    const { subtotal, discountAmount, deliveryFee, total } = calculateTotals();
    
    // Include discount information in the order
    const orderWithDiscount = {
      ...orderData,
      discountCode: discountCodeValid ? discountCode : null,
      subtotalAmount: subtotal,
      discountAmount: discountAmount,
      deliveryFee: deliveryFee,
      totalAmount: total
    };
    
    // Store form data for use in success handler
    const fullOrderData = {
      ...orderData,
      cart: cart,
      isDelivery: isDelivery,
      discountInfo: discountInfo
    };
    
    submitOrderMutation.mutate(orderWithDiscount, {
      onSuccess: (data: any) => {
        // Use the calculated values from calculateTotals (which includes discount)
        const { subtotal: finalSubtotal, discountAmount: finalDiscount, deliveryFee: finalDeliveryFee, total: finalTotal } = calculateTotals();
        
        setOrderDetails({
          ...fullOrderData,
          orderNumber: data.orderNumber,
          subtotal: finalSubtotal,
          discountAmount: finalDiscount,
          deliveryFee: finalDeliveryFee,
          total: finalTotal
        });
        setIsCheckoutOpen(false);
        setIsSuccessOpen(true);
        setCart([]);
        setDiscountCode('');
        clearDiscountCode();
        toast({ title: 'Order placed successfully!' });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card shadow-sm sticky top-0 z-40 border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/images/TopsLogoCrop.png" 
                alt="Tops Supermarket" 
                className="h-10 w-auto"
              />
              <div className="border-l border-border pl-3">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">Restaurant Distribution</h1>
                <p className="text-base md:text-lg text-muted-foreground">Premium products for your business</p>
              </div>
            </div>
          </div>
          {!discountCodeValid && (
            <div className="flex justify-end mt-3">
              <div className="flex items-center gap-2">
                <span className="text-base font-medium text-gray-800">Discount Code</span>
                <Input
                  placeholder="e.g. WH86"
                  value={discountCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    setDiscountCode(value.slice(0, 8));
                  }}
                  maxLength={8}
                  className="w-32 h-12 font-mono text-center text-lg font-bold border-2 border-gray-300 focus:border-emerald-600"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && discountCode.length >= 4) {
                      validateDiscountCode();
                    }
                  }}
                />
                <Button
                  onClick={validateDiscountCode}
                  disabled={discountCode.length < 4}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Hash className="w-4 h-4 mr-1" />
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Discount Code Banner */}
      {discountCodeValid && discountInfo && (
        <div className="bg-emerald-50 border-b border-emerald-200">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-600 text-white rounded-full p-2">
                  <Percent className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-bold text-lg text-emerald-900">
                    {discountInfo.restaurantName}
                  </p>
                  <p className="text-base font-medium text-emerald-800">
                    {discountInfo.tier.toUpperCase()} - Up to {formatPercentage(discountInfo.discountPercentage)} off applied automatically
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearDiscountCode}
                className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100"
              >
                Change Code
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-0 md:px-1 py-2">
        <div className="flex flex-col lg:flex-row gap-2">
          {/* Main Content */}
          <div className="flex-1">
            {/* Category Filters */}
            <Card className="mb-2">
              <CardContent className="p-2">
                <div className="flex gap-2 flex-wrap">
                    <Button
                      className={selectedCategory === 'all' ? 'bg-gray-700 text-white hover:bg-gray-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                      size="sm"
                      onClick={() => setSelectedCategory('all')}
                    >
                      All Products
                    </Button>
                    {categories.map((category) => {
                      const config = RESTAURANT_CATEGORY_CONFIG[category.name];
                      if (!config) return null;
                      const Icon = config.icon;
                      return (
                        <Button
                          key={category.id}
                          className={selectedCategory === category.name ? config.boldColor : config.mildColor}
                          size="sm"
                          onClick={() => setSelectedCategory(category.name)}
                        >
                          <Icon className="w-4 h-4 mr-1" />
                          {config.label}
                        </Button>
                      );
                    })}
                </div>
              </CardContent>
            </Card>

            {/* Products Display */}
            {selectedCategory === 'all' ? (
              // Display by category when showing all
              categories.map((category) => {
                const categoryProducts = productsByCategory[category.name] || [];
                if (categoryProducts.length === 0) return null;
                
                const categoryConfig = RESTAURANT_CATEGORY_CONFIG[category.name];
                if (!categoryConfig) return null;
                
                const Icon = categoryConfig.icon || Package;
                
                return (
                  <div key={category.id} className={`mb-3 p-2 rounded-lg border ${categoryConfig.borderColor}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-2 rounded-full ${categoryConfig.color.split(' ')[0].replace('100', '200')}`}>
                        <Icon className={`w-5 h-5 ${categoryConfig.color.split(' ')[1].replace('800', '700')}`} />
                      </div>
                      <h2 className={`text-xl md:text-2xl font-bold ${categoryConfig.color.split(' ')[1].replace('800', '900')}`}>{categoryConfig.label}</h2>
                      <Badge className={`${categoryConfig.color.replace('100', '200')} border-0 text-xs`}>
                        {categoryProducts.length} products
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
                      <AnimatePresence>
                        {categoryProducts.map((product) => (
                          <RestaurantProductCard
                            key={product.id}
                            product={product}
                            cartItem={getCartItem(product.id)}
                            onAddToCart={addToCart}
                            onUpdateQuantity={updateQuantity}
                            categoryConfig={RESTAURANT_CATEGORY_CONFIG[product.category]}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>
                );
              })
            ) : (
              // Display filtered products
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-5 gap-2">
                <AnimatePresence>
                  {filteredProducts.map((product: any) => (
                    <RestaurantProductCard
                      key={product.id}
                      product={product}
                      cartItem={getCartItem(product.id)}
                      onAddToCart={addToCart}
                      onUpdateQuantity={updateQuantity}
                      categoryConfig={RESTAURANT_CATEGORY_CONFIG[product.category]}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}

            {filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg text-gray-600 font-medium">No products found matching your criteria</p>
              </div>
            )}
          </div>

          {/* Cart Sidebar */}
          <div className="lg:w-[26rem]">  {/* Increased from 20rem (w-80) to 26rem (~30% wider) */}
            <CartSummary
              cart={cart}
              isDelivery={isDelivery}
              onToggleDelivery={setIsDelivery}
              onUpdateQuantity={updateQuantity}
              onCheckout={handleCheckout}
              discountCode={discountCode}
              discountCodeValid={discountCodeValid}
              discountInfo={discountInfo}
              onValidateCode={validateDiscountCode}
              onClearCode={clearDiscountCode}
              onDiscountCodeChange={setDiscountCode}
            />
          </div>
        </div>
      </div>

      {/* Mobile Cart Button */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <Button
          size="lg"
          className="rounded-full shadow-lg bg-emerald-600 hover:bg-emerald-700"
          onClick={handleCheckout}
        >
          <ShoppingCart className="w-5 h-5 mr-2" />
          Cart ({cart.length})
        </Button>
      </div>

      {/* Checkout Modal */}
      {(() => {
        const totals = calculateTotals();
        return (
          <CheckoutModal
            isOpen={isCheckoutOpen}
            onClose={() => setIsCheckoutOpen(false)}
            cart={cart}
            subtotal={totals.subtotal}
            discountCode={discountCodeValid ? discountCode : null}
            discountAmount={totals.discountAmount}
            deliveryFee={totals.deliveryFee}
            total={totals.total}
            isDelivery={isDelivery}
            discountInfo={discountCodeValid ? discountInfo : null}
            onSubmit={handleSubmitOrder}
          />
        );
      })()}

      {/* Success Modal */}
      <OrderSuccessModal
        isOpen={isSuccessOpen}
        onClose={() => setIsSuccessOpen(false)}
        orderDetails={orderDetails}
      />
    </div>
  );
}